// components/VersionForm.jsx
// Form for creating/editing version/release entries
import React, { useState, useEffect } from "react";
import { createVersionEntry, updateVersionEntry, getAllVersions } from "../utils/releaseVersions";
import { getAllLogs } from "../utils/adminLogs";

export default function VersionForm({ open, onClose, existingVersion, onSuccess }) {
  const [version, setVersion] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [changes, setChanges] = useState([{ type: "other", description: "", logEntryId: "" }]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [availableLogs, setAvailableLogs] = useState([]);

  // Load available log entries for linking
  useEffect(() => {
    if (open) {
      getAllLogs().then((logs) => {
        setAvailableLogs(logs);
      });
    }
  }, [open]);

  // Populate form when editing
  useEffect(() => {
    if (existingVersion) {
      setVersion(existingVersion.version || "");
      const date = existingVersion.releaseDate instanceof Date
        ? existingVersion.releaseDate
        : new Date(existingVersion.releaseDate);
      setReleaseDate(date.toISOString().split("T")[0]);
      setChanges(
        existingVersion.changes && existingVersion.changes.length > 0
          ? existingVersion.changes
          : [{ type: "other", description: "", logEntryId: "" }]
      );
      setNotes(existingVersion.notes || "");
    } else {
      // Reset form for new version
      setVersion("");
      setReleaseDate(new Date().toISOString().split("T")[0]);
      setChanges([{ type: "other", description: "", logEntryId: "" }]);
      setNotes("");
    }
  }, [existingVersion, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!version.trim() || !releaseDate) {
      alert("Please fill in version number and release date");
      return;
    }

    setLoading(true);
    try {
      const changesToSave = changes.filter(
        (ch) => ch.description && ch.description.trim()
      );
      
      const versionData = {
        version: version.trim(),
        releaseDate,
        changes: changesToSave,
        notes: notes.trim(),
      };

      if (existingVersion) {
        await updateVersionEntry(existingVersion.id, versionData);
      } else {
        await createVersionEntry(versionData);
      }

      onSuccess();
    } catch (err) {
      console.error("Error saving version:", err);
      alert("Failed to save version: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const addChange = () => {
    setChanges([...changes, { type: "other", description: "", logEntryId: "" }]);
  };

  const removeChange = (index) => {
    setChanges(changes.filter((_, i) => i !== index));
  };

  const updateChange = (index, field, value) => {
    const newChanges = [...changes];
    newChanges[index] = { ...newChanges[index], [field]: value };
    setChanges(newChanges);
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "rgba(28,34,58,0.95)",
          borderRadius: 16,
          padding: 32,
          maxWidth: 700,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          color: "#fff",
          boxShadow: "0 8px 36px 8px #111b",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: "0 0 20px 0", color: "#ffe066" }}>
          {existingVersion ? "Edit Version" : "Create New Version"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
              Version Number *
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 2.0.89"
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(0,0,0,0.3)",
                color: "#fff",
                fontSize: "1em",
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
              Release Date *
            </label>
            <input
              type="date"
              value={releaseDate}
              onChange={(e) => setReleaseDate(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(0,0,0,0.3)",
                color: "#fff",
                fontSize: "1em",
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label style={{ fontWeight: 700 }}>Changes</label>
              <button
                type="button"
                onClick={addChange}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(71,227,115,0.2)",
                  color: "#47e373",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontSize: "0.85em",
                }}
              >
                + Add Change
              </button>
            </div>

            {changes.map((change, index) => (
              <div
                key={index}
                style={{
                  padding: 12,
                  marginBottom: 12,
                  background: "rgba(0,0,0,0.2)",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <select
                    value={change.type}
                    onChange={(e) => updateChange(index, "type", e.target.value)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: "1px solid rgba(255,255,255,0.2)",
                      background: "rgba(0,0,0,0.3)",
                      color: "#fff",
                    }}
                  >
                    <option value="bug">Bug Fix</option>
                    <option value="enhancement">Enhancement</option>
                    <option value="other">Other</option>
                  </select>
                  <select
                    value={change.logEntryId || ""}
                    onChange={(e) => updateChange(index, "logEntryId", e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: "1px solid rgba(255,255,255,0.2)",
                      background: "rgba(0,0,0,0.3)",
                      color: "#fff",
                    }}
                  >
                    <option value="">No link (manual entry)</option>
                    {availableLogs.map((log) => (
                      <option key={log.id} value={log.id}>
                        {log.id}: {log.title}
                      </option>
                    ))}
                  </select>
                  {changes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeChange(index)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 6,
                        border: "1px solid rgba(255,255,255,0.2)",
                        background: "rgba(255,107,107,0.2)",
                        color: "#ff6b6b",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <textarea
                  value={change.description}
                  onChange={(e) => updateChange(index, "description", e.target.value)}
                  placeholder="Describe this change..."
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(0,0,0,0.3)",
                    color: "#fff",
                    fontSize: "0.9em",
                    resize: "vertical",
                  }}
                />
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this release..."
              rows={4}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(0,0,0,0.3)",
                color: "#fff",
                fontSize: "0.9em",
                resize: "vertical",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 20px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "10px 20px",
                borderRadius: 8,
                border: "none",
                background: "#47e373",
                color: "#111",
                fontWeight: 900,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "Saving..." : existingVersion ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
